import router from "@/router";
import api, { UserCredentials } from "@/services/api.service";
import { ActionTree } from "vuex";
import { get } from "lodash";
import { RootState, MatrixState, LoginResult, LoginStatus } from "./types";

export const actions: ActionTree<RootState, RootState> = {
	async initApp({ dispatch, commit }, payload: any) {
		commit("setTimeZoneAbbr", new Date().toLocaleTimeString("en-us", { timeZoneName: "short" }).split(" ")[2]);
		let applicationInformation = (await api.applicationInformation()) ?? {};
		commit("setAppVersion", applicationInformation["version"] ?? "");
		commit("setAccountCreationStatus", await api.getAccountCreationStatus());
		commit("setAppInitialisedFlag", true);

		if (!applicationInformation["server"]) applicationInformation["server"] = await api.retrieveServerName();
		commit("setServerName", applicationInformation["server"] ?? "");
	},

	/**
	 * Logs the user in using the provided loginResult, updates the features list and fetches the list
	 * of available regions if DB federation is enabled.
	 * @param param0
	 * @param loginResult
	 */
	async doLogin({ commit, dispatch }, loginResult: LoginResult): Promise<void> {
		loginResult.isFullUser = true;

		commit("login", loginResult);
		commit("setFeaturesList", loginResult.featuresList);

		if (loginResult.hasOwnProperty("tourNoteRequired")) {
			commit("eventqueue/setTourNotesRequired", loginResult.tourNoteRequired, { root: true });
		}

		if (loginResult.dbFederationEnabled) {
			await dispatch("regions/loadRegions");
		}
	},

	async login({ dispatch }, userCredentials: UserCredentials): Promise<LoginResult> {
		let { data: loginResult } = await api.login(userCredentials);

		if (loginResult.status === LoginStatus.Success) {
			await dispatch("doLogin", loginResult);
			return loginResult;
		} else {
			throw loginResult;
		}
	},

	/**
	 * Checks to see if the user is already logged in. If they are, we update the
	 * store with their login data and return true. Otherwise, this method will
	 * return false.
	 */
	async checkUserAlreadyLoggedIn({ commit, dispatch }) {
		let loginResult;

		try {
			loginResult = await api.checkLogin();
		} catch (err) {
			console.error("Unable to check user login state");
		}

		if (loginResult) {
			await dispatch("doLogin", loginResult);
			return true;
		} else {
			// Clear the current user login
			commit("logout");
			return false;
		}
	},

	async singleEventLogin({ commit }, { sessionID, accessCode }) {
		const loginData = {
			sessionID,
			accessCode
		};

		let { data: loginResult } = await api.singleEventLogin(loginData);

		if (loginResult.status === LoginStatus.Success) {
			loginResult.isFullUser = false;
			commit("login", loginResult);
			return loginResult;
		} else {
			throw loginResult;
		}
	},

	logout({ commit, dispatch }: any) {
		commit("setDataError", false);
		commit("logout");

		const currentRoute = router.currentRoute;

		if (currentRoute && currentRoute.meta && currentRoute.meta.closeOnLogout) {
			window.close();
		} else if (currentRoute.meta.redirectOnLogin) {
			router.push({
				path: `/login?redirect=${currentRoute.fullPath}`
			});
		} else if (currentRoute.fullPath.startsWith("/login?redirect=")) {
			router.push({
				path: `${currentRoute.fullPath}`
			});
		} else {
			router.push({ path: "/login" });
		}

		dispatch("resetState");
	},

	resetState({ commit }) {
		commit("resetState");
		commit("alarmPoints/resetState");
		commit("areaNotes/resetState");
		commit("siteMonitor/resetState");
		commit("tasks/resetState");
		commit("dispatch/resetState");
		commit("userManagement/resetState");
		commit("siteMonitorCameras/resetState");
		commit("sessions/resetState");
		commit("mapLayers/resetState");
		commit("eventSearch/resetState");
		commit("devices/resetState");
		commit("mobile/resetState");
		commit("reports/resetState");
		commit("setup/resetState");
		commit("areas/resetState");
		commit("tours/resetState");
		commit("GenericTable/resetState");
		commit("eventqueue/resetState");
		commit("views/resetState");
		commit("mobileRaiseTemplates/resetState");
		commit("license/resetState");
		commit("videoReporting/resetState");
		commit("metadata/resetState");
		commit("regions/resetState");
		commit("ldapManagement/resetState");
	},

	async serverLogout() {
		return await api.serverLogout();
	},

	async fetchUserGroups({ commit }: any) {
		const userGroups = await api.loadUserGroups();
		commit("setUserGroups", userGroups);
	},

	async searchGroup({ commit }, SearchTerm: string) {
		const userGroups = await api.searchGroup(SearchTerm);
		commit("setUserGroups", userGroups);
	},

	notInEvent() {
		router.push({ path: "/event-queue" });
	},

	async fetchRoutingGroups({ commit }: any) {
		const { data } = await api.loadRoutingGroups(); // MOVE TO EVENT QUEUE STORE
		commit("setRoutingGroups", data);
	},

	/**
	 * @deprecated Please use the mutation instead of the action.
	 * There is no api calls done here.
	 */
	setMapType({ commit }, mapType: string) {
		commit("setMapType", mapType);
	},

	async openMediaMatrix({ commit, getters }) {
		const isEnabled = get(getters.getFeaturesList, ["Alarms", "MediaMatrix"]);
		if (isEnabled) {
			// Only attempt to open the matrix if we're sure it's currently closed
			if (getters.getMediaMatrixState == MatrixState.closed) {
				const tryOpenMediaMatrixWindow = () => {
					const routeData = router.resolve({
						path: "/site-monitor-cameras"
					});
					const mediaMatrixWindow = window.open(
						routeData.href,
						"site-monitor-cameras",
						"location=yes,scrollbars=yes,status=yes"
					);

					const browserBlockedWindowFromOpening = !mediaMatrixWindow;
					if (browserBlockedWindowFromOpening) {
						return false;
					}

					// DOMContentLoaded is too early compared to load event
					mediaMatrixWindow.addEventListener(
						"load",
						event => {
							commit("setMediaMatrixState", MatrixState.open);
							console.log("MediaMatrix state set to open");

							// Attach the beforeunload event listener once we know the matrix has loaded.
							// beforeunload/unload can be called by the blank page as it transitions to ours.
							mediaMatrixWindow.addEventListener(
								"beforeunload",
								event => {
									commit("setMediaMatrixState", MatrixState.closed);
								},
								false
							);
						},
						false
					);

					return true;
				};

				const openMediaMatrixWithRetry = (attemptCount = 0) => {
					if (getters.getMediaMatrixState !== MatrixState.open) {
						if (tryOpenMediaMatrixWindow()) {
							commit("setMediaMatrixState", MatrixState.initializing);
						} else {
							attemptCount++;
							if (attemptCount <= 10) {
								setTimeout(() => {
									openMediaMatrixWithRetry(attemptCount);
								}, 1000);
							}
						}
					}
				};

				if (getters.getMediaMatrixState === MatrixState.closed) {
					openMediaMatrixWithRetry();
				} else if (getters.getMediaMatrixState === MatrixState.initializing) {
					setTimeout(() => {
						if (getters.getMediaMatrixState === MatrixState.initializing) {
							openMediaMatrixWithRetry();
						}
					}, 3000);
				}
			}
		}
	},

	async getInterDataServiceLoginToken(context, regionId: number) {
		return await api.getInterDataServiceLoginToken(regionId);
	},

	async loadFeaturesList({ commit }): Promise<void> {
		const { data } = await api.fetchFeaturesList();
		commit("setFeaturesList", data);
	},

	async getTimeZones({ commit }: any) {
		const timeZones = await api.getTimeZones();
		commit("setTimeZones", timeZones);
	},

	async getUlTypes({ commit }){
		const ulTypes = await api.getUlTypes();
		commit("setUlTypes", ulTypes);
	},

	async loadAccountCreationEnabled({ commit }) {
		var enabled = await api.getAccountCreationStatus();
		commit("setAccountCreationStatus", enabled);
	},

	async setPreviousRoute({ commit }, route: string) {
		commit("setPreviousRoute", route);
	},

	async fetchClipCellsForEvent({ commit }, eventId) {
		const { data } = await api.loadAllClipCellsForEvent(eventId);

		commit("setCurrentClipCells", data.cells);
	},

	async loadLoginPreferences({ commit }) {
		const loginPreferences = await api.getLoginPreferences();
		commit("setLoginPreferences", loginPreferences);
	},

	async clearLoginPreferences({ commit }) {
		commit("setLoginPreferences", null);
	}
};
